'use client'

import { useEffect } from 'react'
import { Calendar, Clock, User, Tag as TagIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { ContentRenderer } from '@/components/content/ContentRenderer'
import { Breadcrumbs } from './Breadcrumbs'
import { TagBadge } from './TagBadge'
import { useLocale } from '@/lib/i18n'
import { useActiveCategoryOverride } from '@/lib/blog/active-category-context'
import type { ArticleDetail as ArticleDetailType, AdjacentArticleInfo } from '@/lib/blog/article-types'

interface ArticleDetailProps {
  article: ArticleDetailType
  locale: string
  prev?: AdjacentArticleInfo | null
  next?: AdjacentArticleInfo | null
}

const LOCALE_MAP: Record<string, string> = {
  cs: 'cs-CZ', en: 'en-US', de: 'de-DE', fr: 'fr-FR',
  it: 'it-IT', nl: 'nl-NL', pl: 'pl-PL', pt: 'pt-PT', sp: 'es-ES',
}

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString(LOCALE_MAP[locale] || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function ArticleDetail({ article, locale, prev, next }: ArticleDetailProps) {
  const { t } = useLocale()
  const { setOverrideSlug } = useActiveCategoryOverride()

  // Build breadcrumbs
  const primaryCategory = article.categories.find(c => c.isPrimary)

  useEffect(() => {
    setOverrideSlug(primaryCategory?.slug ?? null)
    return () => setOverrideSlug(null)
  }, [primaryCategory?.slug, setOverrideSlug])
  const breadcrumbItems = [
    { label: t('blog.breadcrumb_home'), href: '/' },
    { label: t('blog.breadcrumb_blog'), href: `/blog/${locale}` },
    ...(primaryCategory ? [{ label: primaryCategory.name, href: `/blog/${locale}/category/${primaryCategory.slug}` }] : []),
    { label: article.title },
  ]

  return (
    <article className="mx-auto max-w-4xl text-foreground">
    {/* Breadcrumbs */}
    <Breadcrumbs items={breadcrumbItems} />

    {/* Header */}
    <header className="mb-8 mt-8">
      {/* Categories */}
      {article.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {article.categories.map((category) => (
            <Link
              key={category.slug}
              href={`/blog/${locale}/category/${category.slug}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                category.isPrimary
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md hover:shadow-lg'
                  : 'border border-border bg-muted/70 text-muted-foreground hover:border-indigo-400/60 hover:text-foreground'
              }`}
            >
              {category.name}
            </Link>
          ))}
        </div>
      )}

      {/* Title */}
      <h1 className="mb-6 text-3xl font-bold leading-tight text-foreground lg:text-4xl">
        {article.title}
      </h1>

      {/* Metadata bar */}
      <div className="flex flex-wrap items-center gap-4 border-b border-border pb-6 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-indigo-500" />
          {formatDate(article.publishedAt, locale)}
        </span>
        {article.readingTime && (
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4 text-indigo-500" />
            {article.readingTime} {t('blog.min_read')}
          </span>
        )}
        {article.authorName && (
          <span className="flex items-center gap-1.5">
            <User className="h-4 w-4 text-indigo-500" />
            {article.authorName}
          </span>
        )}
      </div>
    </header>

    {/* Content */}
    <div className="max-w-none">
      <ContentRenderer content={article.content} format={article.contentFormat} />
    </div>

    {/* Tags */}
    {article.tags.length > 0 && (
      <div className="mt-12 border-t border-border pt-8">
        <div className="flex items-center gap-2 mb-4">
          <TagIcon className="h-4 w-4 text-indigo-500" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {t('blog.tags')}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            <TagBadge key={tag.slug} tag={tag} locale={locale} />
          ))}
        </div>
      </div>
    )}

    {/* Prev / Next navigation */}
    {(prev || next) && (
      <footer className="mt-10 border-t border-border pt-8">
        <nav className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {prev ? (
            <Link
              href={`/blog/${locale}/${prev.slug}`}
              className="group flex flex-col gap-1 rounded-xl border border-border bg-muted/40 p-5 transition-all duration-200 hover:border-indigo-400/60 hover:bg-muted/70 hover:shadow-sm"
            >
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground group-hover:text-indigo-500 transition-colors">
                <ChevronLeft className="h-3.5 w-3.5" />
                {t('blog.prev_article')}
              </span>
              <span className="line-clamp-2 text-sm font-semibold text-foreground leading-snug">
                {prev.title}
              </span>
            </Link>
          ) : (
            <div />
          )}

          {next ? (
            <Link
              href={`/blog/${locale}/${next.slug}`}
              className="group flex flex-col items-end gap-1 rounded-xl border border-border bg-muted/40 p-5 text-right transition-all duration-200 hover:border-indigo-400/60 hover:bg-muted/70 hover:shadow-sm"
            >
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground group-hover:text-indigo-500 transition-colors">
                {t('blog.next_article')}
                <ChevronRight className="h-3.5 w-3.5" />
              </span>
              <span className="line-clamp-2 text-sm font-semibold text-foreground leading-snug">
                {next.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
        </nav>
      </footer>
    )}
  </article>
  )
}
